import { useEffect, useState } from "react";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { uploadBuilderAssistantPhoto } from "@/lib/manufacturing/builderAssistantClient";
import {
  listQcChecklist,
  listQcChecklistChecks,
  type QcChecklistCategory,
  type QcChecklistCheck,
  type QcChecklistItem,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import { finalQcSignoff, normalizeFinalQcInitials, readFinalQc, readReadyForFinalCert } from "@/lib/manufacturing/finalQc";
import { readHostAgreement } from "@/lib/manufacturing/hostReadyToShipAgreement";
import { passFinalQc, refailFinalQcItem } from "@/lib/manufacturing/qcDashboardClient";
import type { BuilderAssistantObservation } from "@/lib/manufacturing/builderAssistantTypes";

const WALKING = new Set(["active", "tentative", "archive_requested"]);

function resultLabel(check: QcChecklistCheck | undefined, open: boolean): string {
  if (!check) return "Not marked";
  if (check.result === "fail") return open ? "Open failure" : "Failed, then fixed";
  if (check.result === "na") return "Not applicable";
  if (check.result === "pass") return "Passed";
  return "Not marked";
}

export function BuilderAssistantFinalQc({
  sessionId,
  summary,
  observations,
  onChanged,
}: {
  sessionId: string;
  summary: Record<string, unknown>;
  observations: readonly BuilderAssistantObservation[];
  onChanged: () => void | Promise<void>;
}) {
  const [categories, setCategories] = useState<QcChecklistCategory[]>([]);
  const [items, setItems] = useState<QcChecklistItem[]>([]);
  const [checks, setChecks] = useState<QcChecklistCheck[]>([]);
  const [notesRequired, setNotesRequired] = useState(false);
  const [failingId, setFailingId] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initials, setInitials] = useState("");
  const [signed, setSigned] = useState(false);
  const passed = readFinalQc(summary);
  const ready = readReadyForFinalCert(summary);
  const agreement = readHostAgreement(summary);
  const openIds = new Set(
    observations
      .filter((row) => row.observation_type === "QC_DEFECT" && !row.fixed_at)
      .map((row) => row.id),
  );
  const openFailures = openIds.size;
  const checkFor = new Map(checks.map((check) => [check.item_id, check]));

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listQcChecklist(), listQcChecklistChecks(sessionId)]).then(([catalog, sessionChecks]) => {
      if (cancelled) return;
      if (!catalog.ok) {
        setError(catalog.error);
        return;
      }
      if (!sessionChecks.ok) {
        setError(sessionChecks.error);
        return;
      }
      setError(null);
      setCategories(catalog.data.categories);
      setItems(catalog.data.items.filter((item) => WALKING.has(item.status)));
      setNotesRequired(catalog.data.failure_notes_required);
      setChecks(sessionChecks.data.checks);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, observations]);

  async function saveFailure(itemId: string) {
    setBusy(true);
    setError(null);
    const saved = await refailFinalQcItem(sessionId, itemId, note);
    if (!saved.ok) {
      setBusy(false);
      setError(saved.error);
      return;
    }
    if (photo) {
      const uploaded = await uploadBuilderAssistantPhoto(saved.data.observation_id, photo);
      if (!uploaded.ok) {
        setBusy(false);
        setError(`The failure is saved. The photo did not upload: ${uploaded.error}`);
        setFailingId(null);
        setNote("");
        setPhoto(null);
        await onChanged();
        return;
      }
    }
    setBusy(false);
    setFailingId(null);
    setNote("");
    setPhoto(null);
    await onChanged();
  }

  async function passBuilding() {
    setBusy(true);
    setError(null);
    const saved = await passFinalQc(sessionId, initials);
    setBusy(false);
    if (!saved.ok) {
      setError(saved.error);
      return;
    }
    await onChanged();
  }

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Final QC</h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Fail anything that still needs work. That sends the building back to QC and keeps who marked it ready for certification. Final QC Pass signs the building and tells delivery.
        </p>
      </div>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {openFailures > 0 ? (
        <p className="text-sm text-red-800 dark:text-red-200">
          {openFailures} open {openFailures === 1 ? "failure" : "failures"}. QC fixes them, then marks the building ready for final certification again.
        </p>
      ) : passed ? (
        <div className="space-y-1 text-sm">
          <p className="text-green-800 dark:text-green-200">
            {passed.initials ? finalQcSignoff(passed.initials) : "Final QC passed this building. It is ready to ship."}
          </p>
          {agreement ? (
            <p className={agreement.status === "mismatch" ? "text-amber-800 dark:text-amber-200" : "text-green-800 dark:text-green-200"}>
              {agreement.notes.join(" ")}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-800 dark:text-zinc-100">
            {ready
              ? `Ready for final certification${ready.by_label ? ` · ${ready.by_label}` : ""}.`
              : "QC passed this building. It is waiting on final QC."}
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
            <input type="checkbox" checked={signed} disabled={busy} onChange={(event) => setSigned(event.target.checked)} />
            Sign final QC with my initials
          </label>
          <input
            className={`${inputClass} w-24`}
            value={initials}
            maxLength={4}
            placeholder="Initials"
            disabled={busy || !signed}
            onChange={(event) => setInitials(event.target.value.toUpperCase())}
          />
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            disabled={busy || !signed || !normalizeFinalQcInitials(initials)}
            onClick={() => void passBuilding()}
          >
            Final QC Pass
          </button>
        </div>
      )}
      {categories.map((category) => {
        const rows = items.filter((item) => item.category_id === category.id);
        if (rows.length === 0) return null;
        return (
          <div key={category.id} className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{category.name}</h4>
            <ul className="space-y-2">
              {rows.map((item) => {
                const check = checkFor.get(item.id);
                const open = Boolean(check?.observation_id && openIds.has(check.observation_id));
                return (
                  <li key={item.id} className="space-y-2 rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-zinc-100">{item.title}</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-400">{resultLabel(check, open)}</p>
                      </div>
                      {open || failingId === item.id ? null : (
                        <button
                          type="button"
                          className={ghostBtnClass}
                          disabled={busy}
                          onClick={() => {
                            setFailingId(item.id);
                            setNote("");
                            setPhoto(null);
                            setError(null);
                          }}
                        >
                          Fail
                        </button>
                      )}
                    </div>
                    {failingId === item.id ? (
                      <div className="space-y-2">
                        <textarea
                          className={`${inputClass} min-h-14 w-full`}
                          placeholder={notesRequired ? "Describe what failed" : "What still needs work"}
                          value={note}
                          disabled={busy}
                          onChange={(event) => setNote(event.target.value)}
                        />
                        <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
                          {photo ? photo.name : "Optional photo"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            capture="environment"
                            className="hidden"
                            disabled={busy}
                            onChange={(event) => {
                              setPhoto(event.target.files?.[0] ?? null);
                              event.target.value = "";
                            }}
                          />
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void saveFailure(item.id)}>
                            {busy ? "Saving…" : "Save failure"}
                          </button>
                          <button
                            type="button"
                            className={ghostBtnClass}
                            disabled={busy}
                            onClick={() => {
                              setFailingId(null);
                              setPhoto(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
}
